import { v4 as uuid } from '@lukeed/uuid'
import Big from 'big.js'

import { browser } from '$app/environment'
import type {
	Account,
	Category, //CalculatedInvoice,
	Client,
	DocumentDiscount,
	Invoice,
	InvoiceDeposit,
	InvoiceTax,
	InvoiceTaxOptions,
	ItemTax,
	ItemTaxList, //CalculatedInvoiceTax,
	Payment,
	SqlNullString,
	Tax
} from '$lib/types/client_types'

import { type DocumentItem, getEditedAt } from './components/document/doc_types'
import { ListingType } from './components/listings/listings_types'
import { getCurrentDay, getDateAfterDays } from './datetimes'
import { CalculationType, InvoiceStatus, PaymentType, TaxType } from './types'


//export const DEFAULT_COLOR = '#2924bf'
export const DEFAULT_COLOR = '#050505'

/**
 * Since server side auth is not quite sorted, we need data for rendering before we actually have it.
 *
 * We also need data for initial inserts, although this is less clear
 * (db defeaults vs struct defaults vs our defaults here...)
 **/

/*
  Append a 1 to the number or
 */

export function generateId() {
	if (browser) {
		return uuid()
	}
	return ''
}

export function makeSqlNullString(value: string | null | undefined) {
	if (!value || value == null || value == '') {
		const varNullString: SqlNullString = { Valid: false, String: '' }
		return varNullString
	}

	const varNullString: SqlNullString = { Valid: true, String: value }
	return varNullString
}


export function makeDocumentItem(options?: Partial<DocumentItem>): DocumentItem {
	const docItem: DocumentItem = {
		id: '',
		item_id: '',
		idx: 0,
		name: '',
		details: '',
		price: Big(0),
		quantity: Big(1),
		taxes: [], //invoice.taxes.map((tax) => tax.id)
		taxable: true,

		edited_at: getEditedAt()
	}

	options = options || {}
	options.id = options.id || generateId()

	return { ...docItem, ...options }
}

export function makeClient(generateId = uuid): Client {
	return {
		account_id: '',
		id: generateId(),
		archived: false,
		name: '',
		email: '',
		phone: '',
		city: '',
		country: '',
		address_1: '',
		address_2: '',
		postal_code: '',
		state_region: '',
		country_id: 'US',
		language_id: 'en-US',
		information: ''
	}
}

export function makeUUID() {
	if (browser) {
		return uuid()
	}
	return ''
}


//export function makeTax(invoice: Invoice, generateId = uuid): InvoiceTax {
export function makeInvoiceTax(options?: InvoiceTaxOptions): InvoiceTax {
	let it: InvoiceTax = {
		id: '',
		invoice_id: '',
		tax_id: '',
		idx: 0,
		name: '',
		type: TaxType.Exclusive,
		rate: Big(5),
		total: Big(0),
		business_id: ''
	}

	options = options || {}
	options.id = options.id || uuid()

	it = { ...it, ...options }
	return it
}

export function makeTax(generateId = uuid): Tax {
	return {
		id: generateId(),
		name: '',
		rate: Big(5),
		archived: false
	}
}

export function makePercentDiscount(invoice: Invoice, generateId = uuid): DocumentDiscount {
	return {
		id: generateId(),
		name: 'Discount',
		type: CalculationType.Rate,
		rate: Big(10),
		fixed: Big(0)
	}
}

export function makeFixedDiscount(invoice: Invoice, generateId = uuid) {
	return {
		id: generateId(),
		name: 'Discount',
		type: CalculationType.Fixed,
		fixed: Big(50)
	}
}

export function makePercentDeposit(invoice: Invoice, generateId = uuid): InvoiceDeposit {
	return {
		id: generateId(),
		name: 'Deposit Requested',
		type: CalculationType.Rate,
		rate: Big(10),
		fixed: Big(0)
	}
}

export function makeFixedDeposit(invoice: Invoice, generateId = uuid) {
	return {
		id: generateId(),
		name: 'Deposit Requested',
		type: CalculationType.Fixed,
		fixed: Big(10)
	}
}

// export type MakePaymentParams = {
// 	id: string;
// 	number: string;
// 	balance: Big;
// 	deposit_due: Big;
// 	currency: string;
// };
export function makePayment(params: any, generateId = uuid): Payment {
	return {
		id: generateId(),
		total: params.deposit_due.gt(0) ? params.deposit_due : params.balance,
		date: getCurrentDay(),
		method: PaymentType.Manual,
		notes: '',
		currency: params.currency,
		send_receipt: false,
		receipt_sent_at: null,

		// TODO rethink
		invoice_id: params.id,
		invoice_number: params.number
	}
}

export function makeAccount(): Account {
	// Ugly code to solve: "Estimate - stub" error:
	//		ReferenceError: Cannot access 'DEFAULT_COLOR' before initialization at makeAccount
	// let defCOLOR = '#2924bf'
	let defCOLOR = '#050505'
	let defListingType = 'card'
	try {
		defCOLOR = DEFAULT_COLOR
		defListingType = ListingType.rows
	} catch (err) {
		// sentryError('ERROR:makeAccount() => cant set default constant values.')
		console.warn('WARNING:cant set default constant values.')
	}
	return {
		active: false,
		address_1: '',
		address_2: '',
		city: '',
		// color: DEFAULT_COLOR,
		color: defCOLOR,
		company: '',
		country: '',
		created_at: '', // this will cause issues if we use it (can we get rid of them)
		created_at_timestamp: new Date().getTime(),
		disable_branding: false,
		email: '',
		email_visible: '',
		id: '',
		interacted: false, // TODO follow up here
		intercom_account_hash: '',
		invoice_default_heading: '',
		invoice_default_notes: '',
		invoice_default_terms: '',
		invoice_copy_with_client: false,
		logged_at: '',
		logo: '',
		name: '',
		phone: '',
		postal_code: '',
		registered: false,
		registered_at: '',
		registered_at_timestamp: null,
		state_region: '',
		stripe_connect_charges_enabled: false,
		stripe_connect_id: '',
		stripe_customer_id: '',
		stripe_subscription_id: '',
		stripe_subscription_plan_interval: '', // TODO typify
		stripe_subscription_price_id: '',
		stripe_subscription_status: '', // TODO typify
		subscribed: false,
		subscription_period: '', // TODO typify
		taxes: [],
		updated_at: '',
		qs_source: '',
		qs_sended_at: '',
		reminder_invoice_overdue: false,
		reminder_invoice_overdue_days: 0,
		reminder_invoice_overdue_days_1: 0,
		reminder_invoice_overdue_days_2: 0,
		estimate_default_heading: '',
		estimate_default_notes: '',
		estimate_default_terms: '',
		estimate_approve_process: false,
		estimate_copy_with_client: false,
		country_id: 'US',
		currency_id: 'usd',
		notifications_enabled: false,
		default_results_per_page: 25,
		send_automatic_receipt: false,
		stub_show_pay_link: true,
		listing_type: defListingType,
		// listing_type: ListingType.rows
		subscription_plan_key: '',
		site_url: '',
		template_type: '000',
		language_id: 'en-US',
		information: '',
		auth_code_at: '',
		openai_thread_id: '',
		openai_data_consent: false,
		openai_tokens: 0
	}
}

export function makeInvoice(generateId = uuid): Invoice {
	const today = getCurrentDay()
	const invoice: Invoice = {
		// TODO really??
		account_id: '',
		archived: false,

		status: InvoiceStatus.Draft,

		id: generateId(),
		heading: 'Invoice',
		number: '',
		reference: '',
		issued: today,
		due: getDateAfterDays(today, 30),
		color: DEFAULT_COLOR,
		reminder_overdue: false,
		reminder_overdue_days: 0,
		reminder_overdue_days_1: 0,
		reminder_overdue_days_2: 0,
		reminder_sended_at: '',
		notes: '',
		terms: '',
		items: [],
		items_delete: [],
		shipping_items: [],
		shipping_items_delete: [],
		pre_tax_discounts: [],
		taxes: [],
		post_tax_discounts: [],
		deposits: [],
		original_number: '',
		currency_id: 'usd',
		last_sent_at: null,
		last_viewed_at: null,
		last_paid_at: null,
		last_refunded_at: null,
		last_deleted_at: null,
		stub_show_pay_link: false,
		has_attachments: false,
		language_id: 'en-US',
		client_shipping_location_id: null
	}
	return {
		...invoice,
		items: [makeDocumentItem({ idx: 1 })]
	}
}

export const makeCurrency = () => {
	return {
		id: '',
		name: '',
		symbol: '',
		icon: 'usd.svg',
		active: false,
		order: 0
	}
}

export const makeCountry = () => {
	return {
		id: '',
		name: '',
		icon: '',
		currency_id: '',
		currencies_valids: '',
		active: false,
		order: 0,
		christmas: false
	}
}

export const makeLanguage = () => {
	return {
		code: '',
		language: '',
		active: false,
		order: 0
	}
}

// export function makeItem(generateId = uuid): Item {
// 	return {
// 		id: generateId(),
// 		category_id: null,
// 		subcategory_id: null,
// 		name: '',
// 		details: '',
// 		sku: '',
// 		cost: Big(0),
// 		price: Big(0),
// 		taxable: true,
// 		archived: false
// 	}
// }

export function makeItemTax(generateId = uuid): ItemTax {
	return {
		id: generateId(),
		item_id: '',
		tax_id: ''
	}
}

export function makeItemTaxList(generateId = uuid): ItemTaxList {
	return {
		id: generateId(),
		item_id: '',
		tax_id: '',
		name: ''
	}
}

export function makeCategory(): Category {
	return {
		id: '',
		name: '',
		archived: false
	}
}

// export function makeExpenseCategory(): ExpenseCategory {
// 	return {
// 		id: '',
// 		name: '',
// 		archived: false
// 	}
// }

// function getSendReceiptDefault() {
// 	return get(account).send_automatic_receipt
// }

// function stubShowPayLinkDefault() {
// 	if (account) return get(account).stub_show_pay_link
// 	return false
// }
